import { useCallDuration, useSoftphone } from "@dialstack/sdk-react/softphone";
import { ReactNode, useState } from "react";

import AddIcCallIcon from "@mui/icons-material/AddIcCall";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DialpadIcon from "@mui/icons-material/Dialpad";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import PauseIcon from "@mui/icons-material/Pause";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { displayCallerNumber } from "../call-display.util";
import { popCallerNumber } from "../incoming-call-pop.slice";
import AudioDeviceMenu from "./audio-device-menu.component";
import CallNoteField from "./call-note-field.component";
import ConsultTransferBar from "./consult-transfer-bar.component";
import {
  callCardsSx,
  callPopHeaderSx,
  callPopHeaderTextSx,
  callPopSx,
  workbenchControlLabelDisabledSx,
  workbenchControlLabelSx,
  workbenchControlSx,
  workbenchControlsRowSx
} from "./dialstack-softphone-widget.styles";
import DtmfKeypad from "./dtmf-keypad.component";
import IncomingCallPatientCard from "./incoming-call-patient-card.component";
import OutgoingCallPatientCard from "./outgoing-call-patient-card.component";
import PrivateCallControl from "./private-call-control.component";
import SoftphoneCallControls, {
  callBarState
} from "./softphone-call-controls.component";
import SoftphoneSearch from "./softphone-search.component";
import SoftphoneStatusButton from "./softphone-status-button.component";
import TransferTargetPicker from "./transfer-target-picker.component";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];
type Overlay = "keypad" | "addCall" | "devices" | null;

const STATE_LABELS = {
  ringing: "Incoming call",
  calling: "Calling…",
  active: "On a call",
  held: "On hold"
} as const;

export function WorkbenchControl({
  label,
  icon,
  pressed,
  disabled = false,
  onClick
}: {
  label: string;
  icon: ReactNode;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const button = (
    <IconButton
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      sx={workbenchControlSx}
    >
      {icon}
    </IconButton>
  );
  return (
    <Box
      component="span"
      className="suno-phone-no-drag"
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {/* A disabled button fires no events for a tooltip to hang off, and
          the label under it already names it. */}
      {disabled ? button : <Tooltip title={label}>{button}</Tooltip>}
      <Typography
        component="span"
        sx={[
          workbenchControlLabelSx,
          disabled && workbenchControlLabelDisabledSx
        ]}
      >
        {label}
      </Typography>
    </Box>
  );
}

// The unfolded pop once a call is up (ENG-2226): the same plum frame as the
// ringing pop, headed by the on-a-call control row — state, timer and number,
// Mute, End, the fold chevron — the call's card with the chart jumps and Take
// payment, then Suno's own row of call controls in place of the SDK's in-call
// screen: Hold, Keypad, Add call, Transfer, Audio, and Merge / Split for two
// connected calls. Each overlay (keypad, add-call search, devices, the
// colleague picker for a transfer) opens above the row; an attended transfer
// in progress gets its Complete / Cancel bar.
export default function SoftphoneWorkbench({
  call,
  match,
  banner,
  children,
  onFold
}: {
  call: SoftphoneCall;
  // The inbound match for the call, when it rang in.
  match: InboundCallMatch | null;
  // Panel-level notices (the 911 location banner).
  banner?: ReactNode;
  // The unknown-caller card for this call, when it is not a patient's.
  children?: ReactNode;
  onFold: () => void;
}) {
  const {
    connection,
    overlays,
    actions,
    canMerge,
    isMerged,
    mergeCalls,
    splitMerge,
    consultCall
  } = useSoftphone();
  const duration = useCallDuration(call);
  const state = callBarState(call);
  const [overlay, setOverlay] = useState<Overlay>(null);
  const toggle = (next: Exclude<Overlay, null>) => {
    if (overlays.showTransfer) overlays.closeTransfer();
    setOverlay((current) => (current === next ? null : next));
  };
  const toggleTransfer = () => {
    setOverlay(null);
    overlays.toggleTransfer();
  };
  const number = displayCallerNumber(
    match?.pop
      ? popCallerNumber(match.pop)
      : call.direction === "inbound"
        ? call.from
        : call.to
  );
  const isInbound = call.direction === "inbound";

  return (
    <Paper elevation={0} data-testid="softphone-workbench" sx={callPopSx}>
      <Box className="suno-phone-drag-handle" sx={callPopHeaderSx}>
        <SoftphoneStatusButton connection={connection} hasCallActivity />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={callPopHeaderTextSx}>
            {isMerged ? "In conference" : STATE_LABELS[state]}
          </Typography>
          <Typography
            noWrap
            data-testid="workbench-timer"
            sx={{
              fontSize: 12,
              color: "rgba(255, 255, 255, 0.78)",
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {state === "calling"
              ? number
              : [duration, number].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <SoftphoneCallControls call={call} state={state} />
        <Tooltip title="Hide details">
          <IconButton
            aria-label="Hide details"
            className="suno-phone-no-drag"
            size="small"
            onClick={onFold}
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {banner}
      <Box aria-live="polite" sx={callCardsSx}>
        {isInbound ? (
          <IncomingCallPatientCard callIds={[call.id]} heroActions="grid" />
        ) : (
          <OutgoingCallPatientCard callIds={[call.id]} />
        )}
        {children}
      </Box>
      <CallNoteField
        backendCallId={match?.backendCallId ?? null}
        hasPatient={(match?.pop?.patientIds.length ?? 0) > 0}
      />
      {consultCall && <ConsultTransferBar />}
      {overlay === "keypad" && <DtmfKeypad call={call} />}
      {overlay === "addCall" && (
        <Box data-testid="add-call-search">
          <SoftphoneSearch onDialed={() => setOverlay(null)} />
        </Box>
      )}
      {overlay === "devices" && <AudioDeviceMenu />}
      {overlays.showTransfer && <TransferTargetPicker />}
      <Box sx={workbenchControlsRowSx}>
        <WorkbenchControl
          label={state === "held" ? "Resume" : "Hold"}
          icon={<PauseIcon fontSize="small" />}
          pressed={state === "held"}
          onClick={() => actions.callActionsFor(call).toggleHold()}
        />
        <WorkbenchControl
          label="Keypad"
          icon={<DialpadIcon fontSize="small" />}
          pressed={overlay === "keypad"}
          onClick={() => toggle("keypad")}
        />
        <WorkbenchControl
          label="Add call"
          icon={<AddIcCallIcon fontSize="small" />}
          pressed={overlay === "addCall"}
          onClick={() => toggle("addCall")}
        />
        {/* One transfer at a time: the picker closes itself while a consult
            is up, so the control would do nothing. */}
        <WorkbenchControl
          label="Transfer"
          icon={<PhoneForwardedIcon fontSize="small" />}
          pressed={overlays.showTransfer}
          disabled={Boolean(consultCall)}
          onClick={toggleTransfer}
        />
        <WorkbenchControl
          label="Audio"
          icon={<HeadsetMicIcon fontSize="small" />}
          pressed={overlay === "devices"}
          onClick={() => toggle("devices")}
        />
        <PrivateCallControl
          sdkCallId={call.id}
          render={(props) => <WorkbenchControl {...props} />}
        />
        {isMerged ? (
          <WorkbenchControl
            label="Split"
            icon={<CallSplitIcon fontSize="small" />}
            onClick={splitMerge}
          />
        ) : (
          canMerge && (
            <WorkbenchControl
              label="Merge"
              icon={<CallMergeIcon fontSize="small" />}
              onClick={mergeCalls}
            />
          )
        )}
      </Box>
    </Paper>
  );
}
